import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Checkbox from '../Checkbox';
import Price from '../Price';
import CartElementBooking from "./CartElementBooking";
import { formatTimeByTheme, getExcursionsText, getExcursionText } from "../../utils/themeUtils";
import ExcursionDuration from "../ExcursionDuration";
import { Trans, useLingui } from '@lingui/react/macro';
import capitalize from 'lodash/capitalize';
import { priceWithCurrency } from '../../utils/cartUtils';

function CartExcursionEntryBooking(props) {
    const { excursion } = props;
    const cartElements = useSelector(state => state.cart.excursions);
    const currencySymbol = useSelector(state => state.bookings.currencySymbol);

    const {t} = useLingui();

    const [passengers, setPassengers] = useState(() => excursion.passengers.map((passenger) => ({ ...passenger, selected: false })) );
    const [nrSelected, setNrSelected] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);

    const excursionText = getExcursionText();

    useEffect(() => {
        setPassengers(excursion.passengers.map((passenger) => ({ ...passenger, selected: false })));
        setNrSelected(0);
        setErrorMessage("");
        setSelectedIds([]);
    }, [excursion.passengers.length]);

    const handleCheckboxChange = (id) => {
        let nrSelected = 0;
        let errorMessage = "";
        let nrAdultsLeftOnBooking = 0;
        let nrKidsLeftOnBooking = 0;
        let selectedIds = [];

        const updatedPassengers = passengers.map((passenger) => {
            if (passenger.id === id) {
                passenger = { ...passenger, selected: !passenger.selected };
            }
            if (passenger.selected) {
                nrSelected++;
                selectedIds.push(passenger.cartElementId);
            } else {
                if (passenger.ageType === "Adult") {
                    nrAdultsLeftOnBooking++;
                } else {
                    nrKidsLeftOnBooking++;
                }
            }
            return passenger;
        });

        if (nrAdultsLeftOnBooking === 0) {
            if (excursionInCartWithOnlyKids()) {
                errorMessage = t({
                    id: "adults_must_accompany_children_cart_text",
                    message: `Reservation cancellation is not possible. Adults must accompany children. Please remove the children from the shopping cart for this ${excursionText} first.`
                })
            }
            if (nrKidsLeftOnBooking > 0) {
                errorMessage = t({id: "adults_must_accompany_children_text"});
            }
        }

        setPassengers(updatedPassengers);
        setNrSelected(nrSelected);
        setErrorMessage(errorMessage);
        setSelectedIds(selectedIds);
    };

    const excursionInCartWithOnlyKids = () => {
        const itineraryId = excursion.itineraryId;
        const excursionId = excursion.excursionId;
        if (cartElements[itineraryId] && cartElements[itineraryId][excursionId]) {
            for (let passenger of cartElements[itineraryId][excursionId].passengers) {
                if (passenger.ageType === "Adult") {
                    return false;
                }
            }
            return true;
        }
        return false;
    };

    const getRefundValue = () => {
        return passengers.reduce((total, passenger) => {
            if (passenger.selected) {
                return total + passenger.price;
            }
            return total;
        }, 0);
    };

    const tooltipId = "booking-" + excursion.itineraryId + excursion.excursionId + excursion.passengers.join("-");
    const modalContent = (
        <>
            <div className="font-bold">
                <Trans id="cancel_excursion_for_pax">Cancel {getExcursionText()} for the following passengers:</Trans>
            </div>
            <div className="bg-primary-lightest px-4 py-2 mt-4">
                {passengers.map((passenger, index) =>
                    <div className="my-2" key={passenger.id}>
                        <Checkbox
                            label={`${passenger.firstName} ${passenger.lastName}`}
                            checked={passenger.selected}
                            disabled={false}
                            handleCheckboxChange={() => handleCheckboxChange(passenger.id)}
                        />
                    </div>
                )}
            </div>
            <Price preText={t({id: "value"})} price={getRefundValue()} currencySymbol={currencySymbol}/>
            {errorMessage &&
                <div className="errors mt-2">
                    {errorMessage}
                </div>
            }
        </>
    );

    function getBookingTooltipText(price, state = "") {
        const excursionsText = getExcursionsText();
        const priceString = priceWithCurrency(currencySymbol, price);
        return state === "Waitlisted" ?
            t({id: "charged_onboard_if_spot_available"}) :
            capitalize(t({
                id: "excursions_paid_onboard",
                message: `${excursionsText} must be paid onboard ${priceString}`
            }));
    }

    return (
        <div>
            <CartElementBooking
                name={excursion.excursionName}
                tooltipId={tooltipId}
                tooltipText={getBookingTooltipText(excursion.price, excursion.state)}
                linkTo={ t({id: "/excursion"}) + `/${excursion.itineraryId}/${excursion.excursionId}`}
                imageReference={excursion.excursionId}
                imageCategory="cruiseExcursion"
                modalContent={modalContent}
                refundDisabled={nrSelected === 0 || errorMessage}
                refundIds={selectedIds}
                state={excursion.state}
                isIncludedExcursion={excursion.included}
            >
                <ul className="list-reset my-2 md:grow">
                    {excursion.passengers.map((passenger, index) =>
                        <li key={index}>{`${passenger.firstName} ${passenger.lastName}`}</li>)}
                </ul>
                <div className="my-2">
                    <div>{excursion.bookingCode}</div>
                    <div>{formatTimeByTheme(excursion.fromTime)}</div>
                    <ExcursionDuration duration={excursion.duration} />
                </div>
            </CartElementBooking>
        </div>
    );
}

export default CartExcursionEntryBooking;